import { FC } from 'react'
import {
	Badge,
	Box,
	Button,
	ColumnLayout,
	Container,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'

import { useNewPlatformFormStore } from '../../../../stores/platforms/newPlatformFormStore'
import { useGenericTagsManagementFormStore } from '../../../../stores/tags/genericTagsManagementFormStore'
import ApproverOverview from '../../../ControlledFormInputs/ApproverOverview'
import { getTagsApprovalObject } from '../../../../types/tags'

type ReviewField = {
	label: string
	value: unknown
}

const renderValue = (value: unknown): string => {
	if (Array.isArray(value)) {
		const entries = value
			.map((item) => renderValue(item))
			.filter((item) => item !== 'N/A')

		return entries.length > 0 ? entries.join(', ') : 'N/A'
	}

	if (typeof value === 'object' && value !== null) {
		const option = value as { label?: string; value?: string }
		return option.label || option.value || 'N/A'
	}

	if (value === '' || value === undefined || value === null) {
		return 'N/A'
	}

	return String(value)
}

const renderFields = (fields: Array<ReviewField>) => (
	<ColumnLayout columns={2} variant="text-grid">
		{fields.map((field) => (
			<div key={field.label}>
				<Box variant="awsui-key-label">{field.label}</Box>
				{renderValue(field.value)}
			</div>
		))}
	</ColumnLayout>
)

const Review: FC = () => {
	const {
		formValues,
		actions: { setCurrentStep }
	} = useNewPlatformFormStore()

	const { formValues: tagFormValues } = useGenericTagsManagementFormStore()

	const isStandardCluster = formValues?.platformType?.value === 'standard'

	const clusterDetailsFields: Array<ReviewField> = [
		{ label: 'GCP Project', value: formValues.gcpProjectID },
		{ label: 'Environment', value: formValues.environment },
		{ label: 'Deployment', value: formValues.deployment },
		{ label: 'Data Classification', value: formValues.dataClassification },
		{ label: 'Tier Level', value: formValues.tierLevel },
		{ label: 'Region', value: formValues.region },
		{ label: 'Platform Name', value: formValues.platformName },
		{ label: 'Cluster Alias', value: formValues.systemAlias },
		{ label: 'Cluster Name', value: `glo-isg-${formValues.systemAlias}-${formValues.environment.value}-gke` },
		{ label: 'Cluster Owner', value: formValues.platformOwner },
		{ label: 'Cluster Owner Email', value: formValues.platformOwnerEmail },
		{ label: 'Cluster Viewer(s)', value: formValues.platformViewer }
	]

	const clusterStrategyFields: Array<ReviewField> = [
		{ label: 'Cluster Type', value: formValues.platformType },
		{ label: 'Cluster Size', value: formValues.platformSize },
		...(isStandardCluster
			? [
					{ label: 'Minimum Node', value: formValues.minNode },
					{ label: 'Maximum Node', value: formValues.maxNode },
					{ label: 'Machine Type', value: formValues.machineType }
			  ]
			: [])
	]
	
	const resourceTagFields: Array<ReviewField> = [
		{ label: 'Group', value: tagFormValues.group },
		{ label: 'Funding Source', value: tagFormValues.fundingSourceType },
		{ label: 'Data Classification', value: tagFormValues.dataClassification },
		{ label: 'WBS Code', value: tagFormValues.WBSCode },
		{ label: 'Cost Center', value: tagFormValues.costCenter }
	]

	return (
		<SpaceBetween direction="vertical" size="l">
			<Header
				variant="h3"
				actions={<Button onClick={() => setCurrentStep(0)}>Edit</Button>}
			>
				Step 1: Cluster Details
			</Header>
			<Container>{renderFields(clusterDetailsFields)}</Container>

			<Header
				variant="h3"
				actions={<Button onClick={() => setCurrentStep(1)}>Edit</Button>}
			>
				Step 2: Cluster Strategy
			</Header>
			<Container>
				<ColumnLayout columns={2} variant="text-grid">
					<div>
						<Box variant="awsui-key-label">Cluster Type</Box>
						<Badge color="blue">{renderValue(formValues.platformType)}</Badge>
					</div>
					{clusterStrategyFields.slice(1).map((field) => (
						<div key={field.label}>
							<Box variant="awsui-key-label">{field.label}</Box>
							{renderValue(field.value)}
						</div>
					))}
				</ColumnLayout>
			</Container>
			<Header
				variant="h3"
				actions={<Button onClick={() => setCurrentStep(2)}>Edit</Button>}
			>
				Step 3: Resource Tags
			</Header>
			<Container>
				<SpaceBetween direction="vertical" size="m">
					{renderFields(resourceTagFields)}
					<ApproverOverview
						approvalStatus={getTagsApprovalObject()}
						isDisabled
					/>
				</SpaceBetween>
			</Container>
		</SpaceBetween>
	)
}

export default Review
